import { useMemo, useState } from 'react'
import { View, Text, StyleSheet, Modal, Pressable, TouchableOpacity } from 'react-native'
import Svg, { G, Polygon, Polyline, Line, Text as SvgText } from 'react-native-svg'
import { useTranslation } from 'react-i18next'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import { fonts } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { darken, lighten } from '../personAvatarConfig'

// Dettaglio di una salita (GPM) del profilo di tappa: si apre toccando il GPM in StageProfile.
// Dati: salita = { nome, cat, cima, lung, pend, tratti: [[km di fine dal basso, pendenza %], ...] } (dim_race_stage.profile.salite[i]).
// La quota di partenza si ricava dalla cima togliendo il dislivello dei tratti; i colori seguono la legenda del profilo (per pendenza).

const H = 184, PAD_L = 36, PAD_R = 10, PAD_T = 22, PAD_B = 24
const niceStep = (range) => [5, 10, 20, 25, 50, 100, 200].find(s => range / s <= 5) ?? 200
const num = (n, lang, digits = 0) => {
  try { return Number(n).toLocaleString(lang, { minimumFractionDigits: digits, maximumFractionDigits: digits }) }
  catch { return Number(n).toFixed(digits).replace('.', ',') }
}

export default function ClimbDetail({ climb, onClose }) {
  const { t, i18n } = useTranslation()
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const [w, setW] = useState(0)
  const lang = i18n.language
  const brand = tc.rosa
  const TONE = useMemo(() => ({ flat: lighten(brand, 0.78), lo: lighten(brand, 0.45), mid: brand, hi: darken(brand, 0.7) }), [brand])
  const toneOf = (g) => (g < 3 ? 'flat' : g < 6 ? 'lo' : g < 9 ? 'mid' : 'hi')

  const geo = useMemo(() => {
    // senza il dettaglio a tratti: un solo tratto a pendenza media costante (grafico semplificato)
    const tr = climb?.tratti?.length ? climb.tratti : (climb?.lung && climb?.pend != null ? [[climb.lung, climb.pend]] : null)
    if (!tr?.length || !w) return null
    // quote ai bordi dei tratti: si parte dalla base e si sale; l'ultima combacia con la cima
    let gain = 0, k0 = 0
    for (const [k, g] of tr) { gain += (k - k0) * 10 * g; k0 = k }
    const base = climb.cima - gain
    const pts = [[0, base]]
    let e = base; k0 = 0
    for (const [k, g] of tr) { e += (k - k0) * 10 * g; pts.push([k, e]); k0 = k }
    const kmEnd = tr[tr.length - 1][0]
    const lo = Math.min(...pts.map(p => p[1])), hi = Math.max(...pts.map(p => p[1]))
    const step = niceStep(hi - lo || 10)
    const yMin = Math.floor((lo - 4) / step) * step, yMax = Math.ceil((hi + 4) / step) * step
    const x = (km) => PAD_L + (km / kmEnd) * (w - PAD_L - PAD_R)
    const y = (m) => PAD_T + (1 - (m - yMin) / (yMax - yMin)) * (H - PAD_T - PAD_B)
    const yTicks = []
    for (let v = yMin; v <= yMax + 0.01; v += step) yTicks.push(v)
    const segs = tr.map(([k, g], i) => {
      const [ka, ea] = pts[i], [kb, eb] = pts[i + 1]
      const poly = `${x(ka)},${y(yMin)} ${x(ka)},${y(ea)} ${x(kb)},${y(eb)} ${x(kb)},${y(yMin)}`
      return { poly, g, cx: (x(ka) + x(kb)) / 2, width: x(kb) - x(ka), top: Math.min(y(ea), y(eb)), kb }
    })
    return { base, pts, x, y, yTicks, segs, kmEnd, line: pts.map(([k, m]) => `${x(k)},${y(m)}`).join(' ') }
  }, [climb, w])

  if (!climb) return null
  const catLabel = climb.cat === 0 ? 'HC' : climb.cat
  const stats = [
    [t('pick.climb_length'), `${num(climb.lung, lang, 1)} km`],
    [t('pick.climb_avg'), `${num(climb.pend, lang, 1)}%`],
    [t('pick.climb_base'), geo ? `${num(geo.base, lang)} m` : '–'],
    [t('pick.climb_summit'), `${num(climb.cima, lang)} m`],
  ]

  return (
    <Modal visible transparent animationType="fade" onRequestClose={onClose} statusBarTranslucent>
      <Pressable style={styles.backdrop} onPress={onClose} accessibilityRole="button" accessibilityLabel={t('pick.close')}>
        <Pressable style={styles.sheet} onPress={() => {}}>
          <View style={styles.head}>
            <View style={styles.cat}><Text style={styles.catText}>{catLabel}</Text></View>
            <Text style={styles.title} numberOfLines={2}>{String(climb.nome)}</Text>
            <TouchableOpacity onPress={onClose} hitSlop={10} style={styles.close} accessibilityRole="button" accessibilityLabel={t('pick.close')}>
              <MaterialCommunityIcons name="close" size={20} color={tc.text} />
            </TouchableOpacity>
          </View>

          <View style={styles.stats}>
            {stats.map(([label, value]) => (
              <View key={label} style={styles.stat}>
                <Text style={styles.statValue}>{value}</Text>
                <Text style={styles.statLabel}>{label}</Text>
              </View>
            ))}
          </View>

          <View onLayout={e => setW(e.nativeEvent.layout.width)} style={{ height: H }}>
            {geo && (
              <Svg width={w} height={H}>
                {geo.yTicks.map(v => (
                  <G key={`y${v}`}>
                    <Line x1={PAD_L} x2={w - PAD_R} y1={geo.y(v)} y2={geo.y(v)} stroke={tc.border} strokeWidth="0.8" strokeDasharray="3,3" />
                    <SvgText x={PAD_L - 6} y={geo.y(v) + 4} fontSize="11" fontFamily={fonts.condensedRegular} fill={tc.textMuted} textAnchor="end">{Math.round(v)}</SvgText>
                  </G>
                ))}
                {geo.segs.map((s, i) => (
                  <Polygon key={`p${i}`} points={s.poly} fill={TONE[toneOf(s.g)]} stroke={tc.bgCard} strokeWidth="1" />
                ))}
                <Polyline points={geo.line} fill="none" stroke={tc.text} strokeWidth="1.6" strokeLinejoin="round" />
                {geo.segs.map((s, i) => (
                  <SvgText key={`g${i}`} x={s.cx} y={s.top - 6} fontSize={s.width < 34 ? 10 : 12} fontFamily={fonts.condensedBold} fill={tc.text} textAnchor="middle">{`${num(s.g, lang, 1)}%`}</SvgText>
                ))}
                <SvgText x={PAD_L} y={H - 6} fontSize="11" fontFamily={fonts.condensedRegular} fill={tc.textMuted} textAnchor="start">0</SvgText>
                {geo.segs.map((s, i) => (
                  <SvgText key={`k${i}`} x={geo.x(s.kb)} y={H - 6} fontSize="11" fontFamily={fonts.condensedRegular} fill={tc.textMuted} textAnchor={i === geo.segs.length - 1 ? 'end' : 'middle'}>{num(s.kb, lang, 1)}</SvgText>
                ))}
              </Svg>
            )}
          </View>

          <View style={styles.legend}>
            {[['flat', '< 3%'], ['lo', '3–6%'], ['mid', '6–9%'], ['hi', '> 9%']].map(([k, label]) => (
              <View key={k} style={styles.chip}>
                <View style={[styles.swatch, { backgroundColor: TONE[k] }]} />
                <Text style={styles.legendText}>{label}</Text>
              </View>
            ))}
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  backdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'flex-end' },
  sheet: { backgroundColor: tc.bgCard, borderTopLeftRadius: 18, borderTopRightRadius: 18, paddingHorizontal: 16, paddingTop: 14, paddingBottom: 28 },
  head: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 12 },
  cat: { minWidth: 30, height: 26, borderRadius: 6, backgroundColor: tc.text, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 6 },
  catText: { fontFamily: fonts.condensedBold, fontSize: 14, color: tc.bgCard },
  title: { flex: 1, fontFamily: fonts.condensedBold, fontSize: 18, color: tc.text, textTransform: 'uppercase' },
  close: { width: 32, height: 32, alignItems: 'center', justifyContent: 'center' },
  stats: { flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6 },
  stat: { flex: 1 },
  statValue: { fontFamily: fonts.condensedBold, fontSize: 18, color: tc.text },
  statLabel: { fontSize: 11, color: tc.textMuted, marginTop: 1 },
  legend: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, marginTop: 6 },
  chip: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  swatch: { width: 14, height: 8, borderRadius: 3 },
  legendText: { fontSize: 12, color: tc.textMuted },
})
